import { Box, Button, Card, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  TextInput,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../layouts/dashboard';
import { Resources } from '../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import RegenerateDialog from './RegenerateDialog';
import VerifyDialog from './VerifyDialog';
import { RelayDepositRecord } from '../../services/relayDepositService';
import CopyBtn from '../../../../components/button/CopyBtn';
import './init';

const truncate = (value?: string | null, max = 18) => {
  if (!value) return '-';
  if (value.length <= max) return value;
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
};

const AddressCell = ({ value }: { value?: string | null }) => {
  if (!value) return <span>-</span>;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip title={value}>
        <span>{truncate(value, 22)}</span>
      </Tooltip>
      <CopyBtn text={value} showMsg={false} />
    </Stack>
  );
};

const formatTime = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <TextInput
        source="userEvmAddress"
        label={t('module.relay-deposit.userEvmAddress')}
        alwaysOn
        resettable
      />
      <TextInput
        source="userSolAddress"
        label={t('module.relay-deposit.userSolAddress')}
        alwaysOn
        resettable
      />
      <TextInput
        source="depositAddress"
        label={t('module.relay-deposit.depositAddress')}
        alwaysOn
        resettable
      />
    </Filter>
  );
};

const View = () => {
  const t = useTranslate();
  const [regenerateRecord, setRegenerateRecord] = useState<RelayDepositRecord | null>(null);
  const [verifyRecord, setVerifyRecord] = useState<RelayDepositRecord | null>(null);

  return (
    <PermissionGuard resource="relay" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={t('module.relay-deposit.title')}
          links={[
            { name: t('module.relay-deposit.title'), href: `/${Resources.relayDeposit}` },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            filters={<ListFilter />}
            filterDefaultValues={{}}
            perPage={20}
            exporter={false}
            resource={Resources.relayDeposit}
            sort={{ field: 'updatedAt', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-table': {
                    tableLayout: 'fixed',
                    minWidth: 1600,
                    width: '100%',
                  },
                  '& .RaDatagrid-thead th, & .RaDatagrid-tbody td': {
                    whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    verticalAlign: 'middle',
                  },
                }}
              >
                <FunctionField
                  label={t('module.relay-deposit.userEvmAddress')}
                  render={(record: RelayDepositRecord) => <AddressCell value={record.userEvmAddress} />}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.userSolAddress')}
                  render={(record: RelayDepositRecord) => <AddressCell value={record.userSolAddress} />}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.hyperliquidAddress')}
                  render={(record: RelayDepositRecord) => <AddressCell value={record.hyperliquidAddress} />}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.depositAddr')}
                  render={(record: RelayDepositRecord) => <AddressCell value={record.depositAddr} />}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.solDepositAddr')}
                  render={(record: RelayDepositRecord) => <AddressCell value={record.solDepositAddr} />}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.tvmDepositAddr')}
                  render={(record: RelayDepositRecord) => <AddressCell value={record.tvmDepositAddr} />}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.loginTime')}
                  render={(record: RelayDepositRecord) => formatTime(record.loginTime)}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.updatedAt')}
                  render={(record: RelayDepositRecord) => formatTime(record.updatedAt)}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.relay-deposit.operating')}
                  render={(record: RelayDepositRecord) => (
                    <Stack direction="row" spacing={1}>
                      <PermissionGuard resource="relay" action="update" fallback={<></>}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setRegenerateRecord(record)}
                        >
                          {t('module.relay-deposit.regenerate')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setVerifyRecord(record)}
                        >
                          {t('module.relay-deposit.verify')}
                        </Button>
                      </PermissionGuard>
                    </Stack>
                  )}
                  sortable={false}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        {regenerateRecord && (
          <RegenerateDialog
            record={regenerateRecord}
            open
            onClose={() => setRegenerateRecord(null)}
          />
        )}
        {verifyRecord && (
          <VerifyDialog record={verifyRecord} open onClose={() => setVerifyRecord(null)} />
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
